import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { earningsApi, campaignApi } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { TrendingUp, ChevronRight, Target, Users, Sparkles, Clock } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';

const EarnPage = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [earnings, setEarnings] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [activeTab, setActiveTab] = useState('active');
  const [chartPeriod, setChartPeriod] = useState('30d');

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { fetchChartData(); }, [chartPeriod]);

  const fetchData = async () => {
    try {
      const [earningsRes, campaignsRes] = await Promise.all([
        earningsApi.getSummary(),
        campaignApi.getAll(),
      ]);
      setEarnings(earningsRes.data);
      setCampaigns(campaignsRes.data);
    } catch (error) {
      toast.error('Failed to load earnings data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchChartData = async () => {
    try {
      const res = await earningsApi.getChart(chartPeriod);
      setChartData(res.data);
    } catch (error) {
      console.error('Failed to load chart data');
    }
  };

  const getObjectiveIcon = (objective) => {
    switch (objective) {
      case 'user_acquisition': return Users;
      case 'engagement': return Sparkles;
      default: return Target;
    }
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="p-6 lg:p-0 space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-48 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </AppLayout>
    );
  }

  if (user?.user_type !== 'creator') {
    return (
      <AppLayout>
        <div className="min-h-screen lg:min-h-0 pb-24 lg:pb-0">
          <div className="p-6 lg:p-0 lg:mb-6">
            <h1 className="text-2xl lg:text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Earn</h1>
          </div>
          <div className="px-6 lg:px-0">
            <div className="bg-card border border-white/10 rounded-2xl p-8 text-center max-w-lg mx-auto">
              <div className="w-20 h-20 mx-auto rounded-full bg-primary/20 flex items-center justify-center mb-6">
                <TrendingUp className="w-10 h-10 text-primary" />
              </div>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Start earning on LekeeLekee</h2>
              <p className="text-muted-foreground mb-6">Join campaigns, create content, and earn money for your creativity and influence.</p>
              <Button className="bg-primary hover:bg-red-600 text-white font-semibold glow-primary">Apply as Creator</Button>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  const CampaignCard = ({ campaign }) => {
    const Icon = getObjectiveIcon(campaign.objective);
    const progress = ((campaign.budget_total - campaign.budget_remaining) / campaign.budget_total) * 100;
    return (
      <Link
        to={`/campaigns/${campaign.campaign_id}`}
        className="block bg-card border border-white/10 rounded-xl p-4 hover:border-primary/50 transition-colors"
        data-testid={`campaign-${campaign.campaign_id}`}
      >
        <div className="flex items-center gap-4 mb-3">
          {campaign.image_url ? (
            <img src={campaign.image_url} alt="" className="w-12 h-12 rounded-xl object-cover" />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
              <Icon className="w-6 h-6 text-primary" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{campaign.name}</p>
            <p className="text-sm text-muted-foreground capitalize">{campaign.objective.replace('_', ' ')}</p>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
        </div>
        <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-2">
          <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${progress}%` }} />
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{progress.toFixed(0)}% complete</span>
          <span className="text-green-500 font-medium">{formatCurrency(campaign.budget_total - campaign.budget_remaining)} earned</span>
        </div>
      </Link>
    );
  };

  return (
    <AppLayout>
      <div className="min-h-screen lg:min-h-0 pb-24 lg:pb-0">
        {/* Header */}
        <div className="p-6 lg:p-0 lg:mb-6">
          <h1 className="text-2xl lg:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Earnings</h1>
          <p className="text-muted-foreground">Track your creator earnings</p>
        </div>
        
        {/* Desktop: 2-column layout, Mobile: stacked */}
        <div className="lg:grid lg:grid-cols-3 lg:gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Earnings Balance Card */}
            <div className="px-6 lg:px-0">
              <div className="bg-card border border-white/10 rounded-2xl p-6 card-accent relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-primary" />
                <p className="text-muted-foreground mb-2">Total Earnings</p>
                <p className="text-4xl font-bold balance-text mb-4" data-testid="total-earnings">{formatCurrency(earnings?.total_earnings)}</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">This Month</p>
                    <p className="font-medium text-green-500">{formatCurrency(earnings?.this_month)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">All Time</p>
                    <p className="font-medium">{formatCurrency(earnings?.all_time)}</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Earnings Chart */}
            <div className="px-6 lg:px-0">
              <div className="bg-card border border-white/10 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold">Earnings Over Time</h3>
                  <div className="flex gap-1">
                    {['7d', '30d', '90d'].map((period) => (
                      <button
                        key={period}
                        onClick={() => setChartPeriod(period)}
                        className={`px-3 py-1 text-xs rounded-full transition-colors ${chartPeriod === period ? 'bg-primary text-white' : 'bg-white/5 text-muted-foreground hover:text-white'}`}
                      >
                        {period}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="h-48 lg:h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 10 }} tickFormatter={(v) => new Date(v).getDate()} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 10 }} tickFormatter={(v) => `₦${(v / 1000).toFixed(0)}k`} />
                      <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }} formatter={(v) => [formatCurrency(v), 'Earnings']} labelFormatter={(v) => new Date(v).toLocaleDateString()} />
                      <Line type="monotone" dataKey="earnings" stroke="#E02020" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Campaigns */}
            <div className="px-6 lg:px-0">
              <h3 className="font-semibold mb-4">Campaigns</h3>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  <TabsTrigger value="active">Active</TabsTrigger>
                  <TabsTrigger value="completed">Completed</TabsTrigger>
                  <TabsTrigger value="discover">Discover</TabsTrigger>
                </TabsList>
                <TabsContent value="active" className="space-y-3">
                  <div className="lg:grid lg:grid-cols-2 lg:gap-4 space-y-3 lg:space-y-0">
                    {campaigns.filter(c => c.status === 'active').map((c) => <CampaignCard key={c.campaign_id} campaign={c} />)}
                  </div>
                  {campaigns.filter(c => c.status === 'active').length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">No active campaigns</div>
                  )}
                </TabsContent>
                <TabsContent value="completed" className="space-y-3">
                  {campaigns.filter(c => c.status === 'completed').map((campaign) => (
                    <Link key={campaign.campaign_id} to={`/campaigns/${campaign.campaign_id}`} className="block bg-card border border-white/10 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div><p className="font-medium">{campaign.name}</p><p className="text-sm text-green-500">Earned: {formatCurrency(campaign.budget_total - campaign.budget_remaining)}</p></div>
                        <span className="text-xs bg-green-500/20 text-green-500 px-2 py-1 rounded-full">Completed</span>
                      </div>
                    </Link>
                  ))}
                  {campaigns.filter(c => c.status === 'completed').length === 0 && (<div className="text-center py-8 text-muted-foreground">No completed campaigns</div>)}
                </TabsContent>
                <TabsContent value="discover" className="space-y-3">
                  <div className="lg:grid lg:grid-cols-2 lg:gap-4 space-y-3 lg:space-y-0">
                    {campaigns.slice(0, 4).map((campaign) => {
                      const Icon = getObjectiveIcon(campaign.objective);
                      return (
                        <div key={campaign.campaign_id} className="bg-card border border-white/10 rounded-xl p-4">
                          <div className="flex items-center gap-4 mb-3">
                            {campaign.image_url ? <img src={campaign.image_url} alt="" className="w-12 h-12 rounded-xl object-cover" /> : <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center"><Icon className="w-6 h-6 text-blue-500" /></div>}
                            <div className="flex-1 min-w-0"><p className="font-medium truncate">{campaign.name}</p><p className="text-sm text-muted-foreground truncate">{campaign.description?.slice(0, 50)}...</p></div>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Earn up to {formatCurrency(campaign.budget_total * 0.1)}</span>
                            <Button size="sm" className="bg-primary hover:bg-red-600">Apply</Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* Right Column - Stats (Desktop only) */}
          <div className="lg:col-span-1 space-y-6">
            {/* Earnings Breakdown */}
            <div className="px-6 lg:px-0">
              <h3 className="font-semibold mb-4">Breakdown</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Campaigns', value: earnings?.breakdown?.campaigns || 0, color: 'bg-primary' },
                  { label: 'Tips', value: earnings?.breakdown?.tips || 0, color: 'bg-green-500' },
                  { label: 'Brand Deals', value: earnings?.breakdown?.brand_deals || 0, color: 'bg-blue-500' },
                  { label: 'Bonuses', value: earnings?.breakdown?.performance_bonus || 0, color: 'bg-yellow-500' },
                ].map((item) => (
                  <div key={item.label} className="bg-card border border-white/10 rounded-xl p-4">
                    <div className={`w-2 h-2 rounded-full ${item.color} mb-2`} />
                    <p className="text-sm text-muted-foreground">{item.label}</p>
                    <p className="font-semibold">{formatCurrency(item.value)}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="px-6 lg:px-0">
              <div className="grid grid-cols-3 lg:grid-cols-1 gap-3">
                <div className="bg-card border border-white/10 rounded-xl p-4 text-center lg:text-left lg:flex lg:items-center lg:justify-between">
                  <p className="text-xs text-muted-foreground lg:text-sm">Active Campaigns</p>
                  <p className="text-2xl font-bold text-primary">{earnings?.active_campaigns || 0}</p>
                </div>
                <div className="bg-card border border-white/10 rounded-xl p-4 text-center lg:text-left lg:flex lg:items-center lg:justify-between">
                  <p className="text-xs text-muted-foreground lg:text-sm">Pending</p>
                  <p className="text-2xl font-bold text-yellow-500">{formatCurrency(0).split(' ')[0]}</p>
                </div>
                <div className="bg-card border border-white/10 rounded-xl p-4 text-center lg:text-left lg:flex lg:items-center lg:justify-between">
                  <p className="text-xs text-muted-foreground lg:text-sm">Completed</p>
                  <p className="text-2xl font-bold">{earnings?.completed_campaigns || 0}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default EarnPage;
